import { Injectable, inject } from '@angular/core';

import { ContactPersonModel } from '@models/contact-person.model';
import { ArticleModel } from '@modules/article/models/article.model';
import { Adapter } from '@modules/content-api/adapter/adapter';
import { ContactPersonAdapter } from '@modules/content-api/adapter/contact.adapter';
import { MediaAdapter } from '@modules/content-api/adapter/media.adapter';
import { PressKit, PressKitSchema, PressKitSectionSchema } from '@modules/content-api/adapter/press-kit.adapter';
import { PreMediaDetailImageStyle } from '@modules/content-api/domain/image-styles/pre-media-detail-image.style';
import { PreMediaListImageStyle } from '@modules/content-api/domain/image-styles/pre-media-list-image.style';
import { MediaAudioInterface, MediaAudioModel } from '@modules/media/models/media-audio.model';
import { MediaDocumentInterface, MediaDocumentModel } from '@modules/media/models/media-document.model';
import { MediaImageModel } from '@modules/media/models/media-image.model';
import { MediaTechnicalDataInterface, MediaTechnicalDataModel } from '@modules/media/models/media-technical-data-model';
import { MediaVideoInterface, MediaVideoModel } from '@modules/media/models/media-video.model';

import { PreMediaArticleAdapter } from './pre-media-article.adapter';
import { PreMediaImageHelper } from './pre-media-image.helper';

@Injectable({
  providedIn: 'root',
})
export class PreMediaPressKitAdapter implements Adapter<PressKit> {
  private mediaAdapter = inject(MediaAdapter);
  private contactPersonAdapter = inject(ContactPersonAdapter);
  private preMediaArticleAdapter = inject(PreMediaArticleAdapter);
  private preMediaImageHelper = inject(PreMediaImageHelper);

  public parse(data: any): PressKit {
    const result = PressKitSchema.safeParse(data);

    const context = { id: data.id, type: 'presskits' };

    if (!result.success) {
      console.error('Error parsing PressKit page data', result.error);
      throw result.error;
    } else {
      if (result.data.mainImage) {
        result.data.mainImage = new MediaImageModel(
          this.preMediaImageHelper.parseImageWithWatermark(result.data.mainImage, PreMediaListImageStyle, context)
        );
      }
      // gallery images
      if (result.data.imageCount) {
        result.data.images = result.data.images?.map(
          (image) =>
            new MediaImageModel(
              this.preMediaImageHelper.parseImageWithWatermark(image, PreMediaDetailImageStyle, context)
            )
        );
      }
      if (result.data.videoCount) {
        result.data.videos = result.data.videos?.map(
          (video) => new MediaVideoModel(this.mediaAdapter.parse(video, [], context) as MediaVideoInterface)
        );
      }
      if (result.data.documentCount) {
        result.data.documents = result.data.documents?.map(
          (document) => new MediaDocumentModel(this.mediaAdapter.parse(document, [], context) as MediaDocumentInterface)
        );
      }
      if (result.data.technicalDataCount) {
        result.data.technicalDatas = result.data.technicalDatas?.map(
          (document) =>
            new MediaTechnicalDataModel(this.mediaAdapter.parse(document, [], context) as MediaTechnicalDataInterface)
        );
      }
      if (result.data.audioCount) {
        result.data.audios = result.data.audios?.map(
          (audio) => new MediaAudioModel(this.mediaAdapter.parse(audio, [], context) as MediaAudioInterface)
        );
      }
      if (result.data.contactPersons?.length) {
        result.data.contactPersons = result.data.contactPersons.map(
          (contactPerson) => new ContactPersonModel(this.contactPersonAdapter.parse(contactPerson))
        );
      }

      if (result.data.articles?.length) {
        result.data.articles = result.data.articles.map(
          (article) => new ArticleModel({ ...this.preMediaArticleAdapter.parse(article), pressKitId: result.data.id })
        );
      }

      return result.data;
    }
  }

  public parseSection(data: any) {
    const result = PressKitSectionSchema.safeParse(data);
    if (!result.success) {
      console.error('Error parsing Press kit section data', result.error.issues);
      throw result.error;
    } else {
      if (result.data.image) {
        result.data.image = new MediaImageModel(
          this.preMediaImageHelper.parseImageWithWatermark(result.data.image, PreMediaListImageStyle, {
            id: '',
            type: 'presskit',
          })
        );
      }

      return result.data;
    }
  }
}
